import {CustomerDetailError} from '@/lib/customer-detail-error';
import { useQuery } from '@tanstack/react-query';
import { getBrowserClient } from '@/lib/supabase';
import { useHostessStore } from '@/stores/hostess';
import type { HostessVisibleCustomerCategory } from '@sakura-grp/lib';

/** RPC 1 の JSONB 返却型 */
export interface CustomerDetailRpcData {
  /** ホステス視点での表示カテゴリ (RPC が算出して返す) */
  visible_customer_category: HostessVisibleCustomerCategory;
  customer: {
    id: string;
    name: string;
    name_kana: string | null;
    mobile_phone: string | null;
    email: string | null;
    postal_code: string | null;
    address_full: string | null;
    memo: string | null;
    created_at: string;
    allow_dm: boolean;
    allow_phone: boolean;
    allow_message: boolean;
    allow_visit: boolean;
  };
  assigned_hostesses: Array<{
    hostess_id: string;
    assigned_at: string | null;
    name: string;
    status: string;
    profile_image_path: string | null;
    store_id: string | null;
    store_name: string | null;
    store_logo_path: string | null;
  }>;
  hostess_memo: string | null;
  store_relations: Array<{
    store_id: string;
    store_name: string | null;
    store_logo_path: string | null;
    assigned_at: string | null;
    notes: string | null;
  }>;
  keep_bottles: Array<{
    id: string;
    bottle_name: string;
    store_id: string;
    store_name: string | null;
    store_logo_path: string | null;
  }>;
  business_cards: Array<{
    id: number;
    name_kanji: string | null;
    name_furigana: string | null;
    company_name: string | null;
    position: string | null;
    mobile_number: string | null;
    email_address: string | null;
    company_phone: string | null;
    fax: string | null;
    address: string | null;
    postal_code: string | null;
    file: string | null;
    file_back: string | null;
    handwritten_note: string | null;
  }>;
  bookings: Array<{
    id: string;
    booking_date: string;
    reserved_guest_count: string | null;
    status: string;
    store_id: string | null;
    store_name: string | null;
    store_logo_path: string | null;
    has_hostess: boolean;
  }>;
  booking_stats: {
    dohan: number;
    kakuyaku: number;
    yoyaku: number;
  };
  last_visit_date: string | null;
  branches: Array<{
    id: string;
    name: string;
    name_kana: string | null;
    company_name: string | null;
    mobile_phone: string | null;
    store_ids: string[];
    phone_count: number;
    message_count: number;
    letter_count: number;
    visit_count: number;
    allow_phone: boolean | null;
    allow_message: boolean | null;
    allow_dm: boolean | null;
    allow_visit: boolean | null;
  }>;
  parent_referrals: Array<{
    referrer_id: string;
    referrer_name: string;
    store_id: string;
  }>;
  is_my_assigned: boolean;
  is_unassigned_branch: boolean;
}

export function useCustomerDetailRpc(id: string | undefined) {
  const { hostess } = useHostessStore();
  const hostessId = hostess?.id;

  return useQuery<CustomerDetailRpcData>({
    queryKey: ['customer-detail', id, hostessId],
    queryFn: async () => {
      const supabase = getBrowserClient();
      const { data, error } = await supabase.rpc('get_customer_detail', {
        p_customer_id: id!,
        p_hostess_id: hostessId ?? undefined,
      });

      if (error) throw new CustomerDetailError(error);
      const rpc = data as unknown as CustomerDetailRpcData;

      // 名刺画像の署名付き URL を生成
      const imagePaths = rpc.business_cards.flatMap((bc) =>
        [bc.file, bc.file_back].filter(Boolean) as string[]
      );
      if (imagePaths.length > 0) {
        const { data: signedUrls } = await supabase.storage
          .from('bizcard-images')
          .createSignedUrls(imagePaths, 3600);
        if (signedUrls) {
          const urlMap = new Map(
            signedUrls
              .filter((u) => u.signedUrl && u.path)
              .map((u) => [u.path, u.signedUrl])
          );
          for (const bc of rpc.business_cards) {
            if (bc.file) bc.file = urlMap.get(bc.file) ?? bc.file;
            if (bc.file_back) bc.file_back = urlMap.get(bc.file_back) ?? bc.file_back;
          }
        }
      }

      return rpc;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
  });
}
